import { type RefObject, useLayoutEffect } from 'react'

import { type MotionDuration, motion, seconds } from '../tokens'
import { gsap } from './gsap'
import { prefersReducedMotion } from './reduced-motion'

export interface RevealOptions {
  /** Seletor dos itens dentro do elemento. Padrão: filhos diretos. */
  targets?: string
  /** Deslocamento vertical de entrada em px. Padrão: 8. */
  y?: number
  /** Intervalo entre itens em ms. Padrão: 30. Só os 12 primeiros itens animam. */
  stagger?: number
  /** Token de duração. Padrão: `base` (200 ms). */
  duration?: MotionDuration
  /** Atraso em ms. */
  delay?: number
  /**
   * Chave da sessão. Com ela, a revelação acontece uma vez por sessão: voltar à página não
   * anima de novo (design-system §7). Sem ela, anima uma vez por montagem.
   */
  onceKey?: string
  /** Desliga a animação (por exemplo, enquanto os dados carregam). */
  enabled?: boolean
}

const revealedKeys = new Set<string>()

/** Prepara os alvos: filhos diretos ou o seletor, limitados ao teto de stagger. */
function collectTargets(root: HTMLElement, selector: string | undefined): HTMLElement[] {
  const all = selector
    ? Array.from(root.querySelectorAll<HTMLElement>(selector))
    : Array.from(root.children).filter((el): el is HTMLElement => el instanceof HTMLElement)
  return all.slice(0, motion.staggerLimit)
}

/**
 * Revela os itens de `ref` na primeira montagem: opacidade + deslocamento curto, em cascata de
 * 30 ms limitada a 12 itens. Com prefers-reduced-motion, só opacidade em 100 ms.
 */
export function useRevealOnce(
  ref: RefObject<HTMLElement | null>,
  {
    targets,
    y = motion.offset.list,
    stagger = motion.stagger,
    duration = 'base',
    delay = 0,
    onceKey,
    enabled = true,
  }: RevealOptions = {},
): void {
  useLayoutEffect(() => {
    const root = ref.current
    if (!root || !enabled) return
    if (onceKey && revealedKeys.has(onceKey)) return
    const items = collectTargets(root, targets)
    if (items.length === 0) return

    const reduced = prefersReducedMotion()
    const ctx = gsap.context(() => {
      gsap.from(items, {
        opacity: 0,
        y: reduced ? 0 : y,
        duration: reduced ? motion.reducedDuration / 1000 : seconds(duration),
        ease: reduced ? 'none' : motion.ease.entrance,
        stagger: reduced ? 0 : stagger / 1000,
        delay: delay / 1000,
        clearProps: 'opacity,transform',
        // Marca só ao terminar: no StrictMode a primeira execução é revertida antes.
        onComplete: () => {
          if (onceKey) revealedKeys.add(onceKey)
        },
      })
    }, root)
    return () => ctx.revert()
  }, [ref, targets, y, stagger, duration, delay, onceKey, enabled])
}

/** Para testes e para "esquecer" revelações (por exemplo, ao sair da conta). */
export function resetRevealHistory(): void {
  revealedKeys.clear()
}

/** A revelação com esta chave já aconteceu nesta sessão? (AgingBar, KpiTile, DataTable) */
export function hasRevealed(key: string): boolean {
  return revealedKeys.has(key)
}

/** Marca a chave como revelada (chame ao terminar a animação, por causa do StrictMode). */
export function markRevealed(key: string): void {
  revealedKeys.add(key)
}
